import { Head, Link } from "@inertiajs/react"
import {
  Plus,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Target,
  BarChart3,
} from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { AppLayout } from "@/layouts/app-layout"

interface Portfolio {
  id: number
  name: string
  total_value: number
  total_cost_basis: number
  total_return: number
  total_return_percentage: number | null
  total_dividends: number
  portfolio_positions_count: number
  created_at: string
  updated_at: string
}

interface PortfolioSummary {
  total_portfolios: number
  total_value: number
  total_cost_basis: number
  total_return: number
  total_dividends: number
  average_return_percentage: number | null
}

interface PortfoliosIndexProps {
  portfolios: Portfolio[]
  portfolio_summary: PortfolioSummary
}

export default function PortfoliosIndex({
  portfolios,
  portfolio_summary,
}: PortfoliosIndexProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  const formatPercentage = (percentage: number | null | undefined) => {
    if (percentage === null || percentage === undefined || isNaN(percentage)) {
      return "0.00%"
    }
    // Additional safety check in case percentage is not a number
    const numPercentage =
      typeof percentage === "number"
        ? percentage
        : parseFloat(String(percentage))
    if (isNaN(numPercentage)) {
      return "0.00%"
    }
    return `${numPercentage >= 0 ? "+" : ""}${numPercentage.toFixed(2)}%`
  }

  const getReturnColor = (return_percentage: number | null | undefined) => {
    if (
      return_percentage === null ||
      return_percentage === undefined ||
      isNaN(return_percentage)
    ) {
      return "text-gray-600"
    }
    if (return_percentage > 0) return "text-green-600"
    if (return_percentage < 0) return "text-red-600"
    return "text-gray-600"
  }

  const getReturnBadge = (return_percentage: number | null | undefined) => {
    if (
      return_percentage === null ||
      return_percentage === undefined ||
      isNaN(return_percentage)
    ) {
      return (
        <Badge className="border-gray-300 bg-gray-100 text-gray-800">
          {formatPercentage(return_percentage)}
        </Badge>
      )
    }
    if (return_percentage > 0) {
      return (
        <Badge className="border-green-300 bg-green-100 text-green-800">
          <TrendingUp className="mr-1 h-3 w-3" />
          {formatPercentage(return_percentage)}
        </Badge>
      )
    }
    if (return_percentage < 0) {
      return (
        <Badge className="border-red-300 bg-red-100 text-red-800">
          <TrendingDown className="mr-1 h-3 w-3" />
          {formatPercentage(return_percentage)}
        </Badge>
      )
    }
    return (
      <Badge className="border-gray-300 bg-gray-100 text-gray-800">
        {formatPercentage(return_percentage)}
      </Badge>
    )
  }

  return (
    <AppLayout>
      <Head title="Portfolios" />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Portfolios</h1>
            <p className="text-muted-foreground">
              Track your investment performance and dividend income
            </p>
          </div>
          <Button asChild>
            <Link href="/portfolios/new">
              <Plus className="mr-2 h-4 w-4" />
              New Portfolio
            </Link>
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Value</CardTitle>
              <DollarSign className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(portfolio_summary.total_value)}
              </div>
              <p className="text-muted-foreground text-xs">
                Across {portfolio_summary.total_portfolios} portfolios
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Return
              </CardTitle>
              <TrendingUp className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(portfolio_summary.total_return)}
              </div>
              <p className="text-muted-foreground text-xs">
                {formatPercentage(portfolio_summary.average_return_percentage)}{" "}
                avg return
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Dividends
              </CardTitle>
              <Target className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(portfolio_summary.total_dividends)}
              </div>
              <p className="text-muted-foreground text-xs">
                Passive income received
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Cost Basis</CardTitle>
              <BarChart3 className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(portfolio_summary.total_cost_basis)}
              </div>
              <p className="text-muted-foreground text-xs">Total invested</p>
            </CardContent>
          </Card>
        </div>

        {/* Portfolios List */}
        {portfolios.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {portfolios.map((portfolio) => (
              <Card
                key={portfolio.id}
                className="transition-shadow hover:shadow-md"
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{portfolio.name}</CardTitle>
                    {getReturnBadge(portfolio.total_return_percentage)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Current Value
                      </span>
                      <span className="font-medium">
                        {formatCurrency(portfolio.total_value)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Cost Basis</span>
                      <span className="font-medium">
                        {formatCurrency(portfolio.total_cost_basis)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Total Return
                      </span>
                      <span
                        className={`font-medium ${getReturnColor(portfolio.total_return_percentage)}`}
                      >
                        {formatCurrency(portfolio.total_return)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Dividends</span>
                      <span className="font-medium">
                        {formatCurrency(portfolio.total_dividends)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Positions</span>
                      <span className="font-medium">
                        {portfolio.portfolio_positions_count}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className="flex-1"
                    >
                      <Link href={`/portfolios/${portfolio.id}`}>View</Link>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className="flex-1"
                    >
                      <Link href={`/portfolios/${portfolio.id}/analytics`}>
                        Analytics
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <BarChart3 className="text-muted-foreground mb-4 h-16 w-16" />
              <h3 className="mb-2 text-lg font-semibold">No portfolios yet</h3>
              <p className="text-muted-foreground mb-6 text-center">
                Create your first portfolio to start tracking your investments
                and dividend income.
              </p>
              <Button asChild>
                <Link href="/portfolios/new">
                  <Plus className="mr-2 h-4 w-4" />
                  Create Portfolio
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  )
}
